import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, MessageCircle, Car } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "./ui/sheet";
import { useSite } from "../lib/SiteContext";
import { useEnquiry } from "../lib/EnquiryContext";
import { waLink } from "../lib/api";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/tours", label: "Tours" },
  { to: "/customize", label: "Customize" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const { content } = useSite();
  const { openEnquiry } = useEnquiry();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 w-full bg-white/85 backdrop-blur-xl border-b border-gray-100 transition-shadow duration-300 ${scrolled ? "shadow-soft" : ""}`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo">
            <img src="/assets/aloha-logo.png" alt="Aloha Corfu Tours logo" className="h-10 w-10 md:h-12 md:w-12 rounded-full" />
            <span className="font-display text-lg md:text-xl font-semibold text-pine leading-tight">
              Aloha Corfu <span className="text-seafoam-dark italic">Tours</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-full transition-colors duration-200 ${
                    isActive ? "text-pine bg-seafoam-light" : "text-gray-600 hover:text-pine hover:bg-gray-50"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="https://alohacorfutransfers.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-book-transfer-button"
              className="hidden xl:inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-4 py-2 text-sm font-semibold text-pine transition-colors duration-200 hover:bg-gold/20"
            >
              <Car className="h-4 w-4" />
              Book a Transfer
            </a>
            <a
              href={waLink(content?.settings)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-button"
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-pine/20 px-4 py-2 text-sm font-semibold text-pine transition-colors duration-200 hover:bg-seafoam-light"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
            <button
              onClick={() => openEnquiry()}
              data-testid="header-enquire-button"
              className="hidden sm:inline-flex rounded-full bg-pine px-5 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-pine-light"
            >
              Enquire Now
            </button>

            <Sheet>
              <SheetTrigger asChild>
                <button
                  className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full text-pine hover:bg-seafoam-light"
                  data-testid="mobile-menu-button"
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-sm bg-white p-0" data-testid="mobile-menu">
                <div className="flex items-center gap-3 border-b border-gray-100 p-5">
                  <img src="/assets/aloha-logo.png" alt="Aloha Corfu Tours logo" className="h-10 w-10 rounded-full" />
                  <span className="font-display text-lg font-semibold text-pine">Aloha Corfu Tours</span>
                </div>
                <nav className="flex flex-col p-4">
                  {NAV.map((item) => (
                    <SheetClose asChild key={item.to}>
                      <NavLink
                        to={item.to}
                        data-testid={`mobile-nav-link-${item.label.toLowerCase()}`}
                        className={({ isActive }) =>
                          `rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                            (item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to))
                              ? "bg-seafoam-light text-pine"
                              : "text-gray-700 hover:bg-gray-50"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                  <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-4">
                    <SheetClose asChild>
                      <button
                        onClick={() => openEnquiry()}
                        data-testid="mobile-enquire-button"
                        className="rounded-full bg-pine px-5 py-3 text-sm font-semibold text-white"
                      >
                        Enquire Now
                      </button>
                    </SheetClose>
                    <a
                      href="https://alohacorfutransfers.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="mobile-book-transfer-button"
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/60 bg-gold/10 px-5 py-3 text-sm font-semibold text-pine"
                    >
                      <Car className="h-4 w-4" /> Book a Transfer
                    </a>
                    <a
                      href={waLink(content?.settings)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="mobile-whatsapp-button"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3 text-sm font-semibold text-white"
                    >
                      <MessageCircle className="h-4 w-4" /> Contact Us on WhatsApp
                    </a>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
